import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../features/auth/session';

/**
 * The auth gate (INV-T23).
 *
 * Protected children render only when the session is confirmed signed-in.
 *
 * The 'checking' state renders NEITHER the protected screen nor a redirect.
 * That is the whole point: at startup the stored session is read
 * asynchronously, so "we do not know yet" is a real state, and treating it as
 * signed-out would bounce a signed-in user to sign-in on every reload, while
 * treating it as signed-in would flash protected content to someone who is not.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'checking') return <SessionCheck />;

  if (status === 'signed-out') {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/**
 * Shown only while the stored session is being resolved. Deliberately plain and
 * wordless — it is usually on screen for a few milliseconds, and a spinner or
 * a "Loading Crewlink…" line would flash distractingly.
 */
export function SessionCheck() {
  return <div className="cl-screen" aria-busy="true" data-testid="session-check" />;
}

/**
 * The inverse gate, for sign-in and password-reset request screens: once
 * signed in, there is no reason to look at a sign-in form.
 */
export function SignedOutOnly({ children }: { children: ReactNode }) {
  const { status } = useSession();

  if (status === 'checking') return <SessionCheck />;
  if (status === 'signed-in') return <Navigate to="/" replace />;

  return <>{children}</>;
}
